<template>
  <div :class="classList">
    <st-breadcrumb :items="breadCrumbItems" v-if="breadCrumbItems" />
    <div class="st-card__wrapper" id="loading-screen" v-if="isLoading">
      <div class="loading-screen-container">
        <st-spinner
          :loadingMessage="loadingMessage"
          :ariaLabel="ariaLabel"
        ></st-spinner>
      </div>
    </div>
    <div class="st-card__wrapper" v-else>
      <slot>default slot</slot>
      <st-error
        v-if="error"
        :error="error"
        :errorMessage="errorMessage"
        :unhandledErrorMessage="unhandledErrorMessage"
      />
    </div>
  </div>
</template>

<script>
import StSpinner from "../spinner/Spinner.vue";
import StError from "../error/Error.vue";
import StBreadcrumb from "../breadcrumb/Breadcrumb";
export default {
  name: "st-card",
  components: {
    StSpinner,
    StError,
    StBreadcrumb,
  },
  props: {
    size: {
      type: String,
      default: "lg",
      validator: function (size) {
        return ["lg", "md", "sm", "xs", "mega", "full"].includes(size);
      },
    },
    widthSize: {
      type: String,
      default: "sm-width",
      validator: function (widthSize) {
        return [
          "xs-width",
          "sm-width",
          "md-width",
          "lg-width",
          "mega-width",
          "full-width",
        ].includes(widthSize);
      },
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    loadingMessage: {
      type: String,
      default: undefined,
    },
    error: {
      default: undefined,
    },
    errorMessage: {
      type: String,
      default: "",
    },
    unhandledErrorMessage: {
      type: String,
      default: "",
    },
    ariaLabel: {
      type: String,
      default: "",
    },
    isModal: {
      type: Boolean,
      default: false,
    },
    breadCrumbItems: {
      type: Array,
    },
    hideOverflow: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    classList: function () {
      return [
        "st-card",
        this.size,
        this.widthSize,
        this.isLoading && "loading",
        this.isModal && "st-card--is-modal",
        this.hideOverflow && "st-card--is-overflow-hidden",
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";
.st-card {
  border-radius: $xl;
  h1,
  h2,
  h3,
  h4 {
    font-family: "Bitter";
    font-weight: bold;
    letter-spacing: rem-calc(1.06667);
    text-align: left;
    color: $dark-grey;
    margin-top: 0;
  }
  p {
    // We have a p selector in main.scss in organization that overwrites the styling of p tags in components
    text-align: left !important;
  }
  &--is-modal {
    h1,
    h2,
    h3,
    h4 {
      font-family: "Source Sans Pro";
      font-weight: bold;
      text-align: center;
      color: $dark-grey;
    }
    p {
      // We have a p selector in main.scss in organization that overwrites the styling of p tags in components
      text-align: center !important;
    }
  }
  &--is-overflow-hidden {
    overflow: hidden;
  }

  @media (max-width: 575px) {
    h3 {
      font-size: 1.5em !important;
    }
  }

  &.lg {
    & > .st-card__wrapper {
      padding: $xl $lg $xxl $lg;
      @include medium {
        padding: $xxl;
      }
    }
    background-color: #ffffff;
    box-shadow: 0 1em 1.5em 0 rgba(43, 36, 29, 0.08);
  }

  &.md {
    & > .st-card__wrapper {
      padding: 0.25em 0.375em;
    }
    background-color: #f9f8f5;
  }

  &.xs {
    & > .st-card__wrapper {
      padding: 2em;
    }
    background-color: #f9f8f5;
  }

  .footer {
    border-bottom-left-radius: 0.375em;
    border-bottom-right-radius: 0.375em;
    margin: 1.75em -0.75em -1.75em -0.75em;
    padding: 0.75em 1em;
    background-color: #f9f8f5;
    display: flex;

    .st-form-group {
      margin-top: 0em;
    }

    button {
      margin-top: 0em;
    }

    > :not(:last-child) {
      margin-right: 0.5em;
    }

    > :not(:first-child) {
      margin-left: 0.5em;
    }
  }

  @media (min-width: 700px) {
    .footer {
      border-bottom-left-radius: 1.5em;
      border-bottom-right-radius: 1.5em;
      margin: 3em -3em -3em -3em;
      padding: 3em;
      flex-wrap: nowrap;
    }
  }

  @media (min-width: 768px) {
    &.md {
      & > .st-card__wrapper {
        padding: 1em 1.5em;
      }
      background-color: #f9f8f5;
    }
  }

  @media (min-width: 992px) {
    &.xs-width {
      max-width: 23.5em;
    }

    &.sm-width {
      max-width: 33.75em;
    }

    &.md-width {
      max-width: 36em;
    }

    &.lg-width {
      max-width: 48.5em;
    }
    &.mega-width {
      max-width: 88.25em;
    }
    &.full-width {
      width: 100%;
    }
  }
}

.error-message {
  word-wrap: normal;
  padding: 0em 2em;
  p {
    opacity: 1;
    margin-bottom: 0em;
    margin-top: 0em;
    color: #cc4039;
    font-size: 0.875em;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.71;
    letter-spacing: normal;
    text-align: left;
  }
}
</style>
